// Section file — populated during component conversion.
// Sections: navbar, header, pricing, faq, testimonials, footer
import React from "react";

const lang = {
  navbar: {
    holidays: {
      1: "NEW YEAR SALE",
      2: "VALENTINES DAY SALE",
      3: "ST PATRICKS DAY SALE",
      4: "SPRING SALE",
      5: "MAY MADNESS SALE",
      6: "SUMMER SALE",
      7: "JULY SALE",
      8: "BACK TO SCHOOL SALE",
      9: "FALL SPECIAL SALE",
      10: "HALLOWEEN SALE",
      11: "BLACK FRIDAY SALE",
      12: "HOLIDAY SEASON SALE",
    },
    fiftyOffAllPlans: "50% OFF ALL PLANS",
    logoAlt: "Chart icon",
    pricing: "Pricing",
    faq: "FAQ",
    reviews: "Reviews",
    logIn: "Log In",
    getStarted: "Start growing",
    dashboard: "Dashboard",
    toggleMenu: "Toggle menu",
  },
  header: {
    trustedBy: "Trusted by 50,000+ creators",
    // Mobile-first hero copy: what they get, price, monthly, no password, where to click.
    heroHeadline: "Grow Your Following on Instagram",
    heroSub: "Our team helps put your profile in front of people interested in your content—helping you grow with real followers every month.",
    heroCta: "Start Growing",
    trustHeading: "Trusted by 50,000+ Creators and Brands",
    trustSub: "Creators and brands worldwide trust us to grow their accounts.",
    trustStatFollowers: "9M+",
    trustStatFollowersLabel: "Followers delivered",
    trustStatRating: "4.9/5",
    trustStatRatingLabel: "From 2,400+ reviews",
    trustStatCreators: "50,000+",
    trustStatCreatorsLabel: "Creators served",
    proofRating: "4.9/5",
    proofReviews: "2,400+ reviews",
    proofCreators: "50,000+ creators",
    proofFollowers: "9M+ followers delivered",
    titlePre: "Real, Organic",
    titlePost: "Followers",
    subtitle: "Gain thousands of real, engaged followers every month through personalized AI targeting.",
    getStarted: "Start growing",
    noPassword: "No password",
    cancelAnytime: "Cancel anytime",
    resultsWithin24: "Growth starts instantly",
    card1Title: "How We Grow Your Account",
    card1Sub: "We put your profile in front of people who actually care about what you post.",
    card1Items: [
      "We find and reach people interested in your content",
      "We handle everything — you just keep posting",
      "No password required",
      "Growth starts instantly",
    ],
    card2Title: "Everything Included",
    card2Sub: "Extra tools to help you create better content and track your growth.",
    card2Items: [
      "AI Content Assistant",
      "Growth analytics dashboard",
      "Profile optimization tools",
      "4K media downloader",
    ],
    platformLabel: "Instagram Growth Service",
    dashboardLabel: "Analytics Dashboard",
    dashboardTitle: "Your all-in-one growth command center",
    dashboardSub: "Tell us exactly who to target — interests, location, and how fast you want to grow. Change it anytime from your dashboard.",
    viewPricing: "View Pricing",
    seePricing: "Choose Your Plan",
    watchHowItWorks: "Watch How It Works",
    featuredIn: "Featured In",
  },
  pricing: {
    heading: "Choose how fast you want to grow",
    sub: (
      <>
        <span className="stripe-phrase">No bots. No fake accounts.</span>{" "}
        <span className="stripe-phrase">Real, engaged growth.</span>
      </>
    ),
    skippedInvoice: (
      <>You have an outstanding invoice for this month. <a href="/dashboard/settings?tab=invoices" className="text-danger text-decoration-underline">Please pay your invoice</a> to continue using our services.</>
    ),
    socialProof: "Trusted by 50,000+ creators and brands",
    mostPopular: "Most Popular",
    plans: {
      pro: {
        name: "Pro Plan",
        blurb: "For serious brands that want the fastest, highest-priority growth.",
        cta: "Start Growing",
        features: [
          { feature: "40,000+ Followers", description: "Accelerated, continuous follower growth delivered gradually over time." },
          { feature: "Start Growing Within 24 hours", description: "Natural, gradual growth that maintains momentum without spikes." },
          { feature: "Niche-targeted Followers", description: "Real, engaged followers matched to your niche from our global network of active users." },
          { feature: "Growth dashboard access", description: "Track your follower growth, monitor progress, and view detailed analytics at any time." },
          { feature: "Enhanced Engagement Activity", description: "Increased likes, views, and interactions to keep your posts active and visible." },
          { feature: "AI Targeting", description: "Smart targeting that surfaces your content to high-intent users most likely to engage." },
          { feature: "AI Content Tools", description: "Unlimited access to AI-powered caption generator, trend analyzer, post reviewer and content idea generator." },
          { feature: "Dedicated Account Manager", description: "A dedicated growth specialist assigned to your account for personalized strategy and priority support." },
        ],
      },
      basic: {
        name: "Basic Plan",
        blurb: "Best for creators who want faster, more consistent growth.",
        cta: "Start Growing",
        features: [
          { feature: "10,000+ Followers", description: "Consistent follower growth delivered throughout your billing cycle." },
          { feature: "Start Growing Within 24 hours", description: "Natural, gradual growth that maintains momentum without spikes." },
          { feature: "Real Organic Followers", description: "Real, organic followers from our global network of active users." },
          { feature: "Growth dashboard access", description: "Track your follower growth, monitor progress, and view detailed analytics at any time." },
          { feature: "Enhanced Engagement Activity", description: "Increased likes, views, and interactions to keep your posts active and visible." },
          { feature: "AI Targeting", description: "Smart targeting that surfaces your content to high-intent users most likely to engage." },
          { feature: "AI Content Tools", description: "Unlimited access to AI-powered caption generator, trend analyzer, post reviewer and content idea generator." },
        ],
      },
      starter: {
        name: "Starter Plan",
        blurb: "A simple way to start growing.",
        cta: "Start Growing",
        features: [
          { feature: "3,000+ Followers", description: "Light, consistent follower growth delivered gradually over time." },
          { feature: "Start Growing Within 24 hours", description: "Natural, gradual growth that maintains momentum without spikes." },
          { feature: "Real Organic Followers", description: "Real, organic followers from our global network of active users." },
          { feature: "Growth dashboard access", description: "Track your follower growth, monitor progress, and view detailed analytics at any time." },
        ],
      },
    },
    perMo: "/mo",
    onlyPerDay: (n) => `Only $${n}/day`,
    fiftyOff: "50% OFF",
    linkYourProfileBtn: "Link your Profile",
    linkYourProfileTitle: "Link Your Profile",
    trustLine: (
      <>
        <span className="stripe-phrase">Cancel anytime ·</span>{" "}
        <span className="stripe-phrase">Secure checkout ·</span>{" "}
        <span className="stripe-phrase">Instant setup</span>
      </>
    ),
    guaranteeHeading: "Guaranteed results or your money back",
    guaranteeBody: "We deliver the promised followers or refund the difference — no questions asked.",
    noPasswordRequired: "No password required",
    noContracts: "No contracts",
    cancelAnytime: "Cancel anytime",
    scheduledFor: (date) => `Scheduled for ${date}`,
    unknown: "Unknown",
    currentPlan: "Current Plan",
    switchToMonthly: "Switch to monthly",
    downgrade: "Downgrade",
    upgrade: "Upgrade",
    selected: "Selected",
    confirmPlanChange: "Confirm Plan Change",
    planChangeDetails: "Plan Change Details",
    profileAlt: "Profile",
    accountLabel: (platform) => `${platform} Account`,
    currentPlanLabel: "Current Plan:",
    newPlanLabel: "New Plan:",
    billingIntervalLabel: "Billing Interval:",
    priceLabel: "Price:",
    planTitle: (name) => `${name} Plan`,
    pricePerMonth: (p) => `$${p}/month`,
    billingInformation: "Billing Information",
    proratedRefund: (value, days) => (
      <>Your new plan will be activated immediately. We'll add a prorated refund of <span className="font-semibold">${value}</span> to your account based on your remaining subscription time ({days} days).</>
    ),
    billingCycleReset: "Your billing cycle will reset to today, with future charges occurring on this date.",
    paymentMethod: "Payment Method",
    addNewCard: "Add New Card",
    cardNumberLabel: "Card Number",
    expiryLabel: "Expiry",
    cvvLabel: "CVV",
    expPlaceholder: "MM/YY",
    errSelectPayment: "Please select a payment method",
    errFillCardDetails: "Please fill in all card details",
    errUpgrading: (e) => `Error upgrading subscription: ${e}`,
    errChangePlanFallback: "Failed to change plan",
    cancel: "Cancel",
    processing: "Processing...",
    confirmChanges: "Confirm Changes",
  },
  faq: {
    label: "FAQ",
    heading: "Common Questions",
    sub: "Everything you need to know before getting started.",
    items: [
      {
        title: "Are the followers real?",
        content: "Yes. Our team of experts grow your audience with real accounts and real engagement — never bots.",
      },
      {
        title: "Do I need to give you my password?",
        content: "No. We never ask for your password — we only need your public username. Your account stays entirely under your control, and you can stop at any time.",
      },
      {
        title: "Is it safe to use?",
        content: "Yes. We never need your password — just your public username. Thousands of creators and businesses grow with us every day.",
      },
      {
        title: "What is ViralGPT?",
        content: "We're a growth team for creators, brands, and businesses. You pick a follower package, we deliver it to your account — and your dashboard adds AI tools for captions, trends, and analytics.",
      },
      {
        title: "What do I have to do?",
        content: "After signing up, give us your Instagram username and pick a plan. We take it from there — you keep posting, and you stay in full control of your account.",
      },
      {
        title: "How fast will I see results?",
        content: "Many users begin seeing increased engagement and follower growth within the first few days depending on their niche, activity, and consistency.",
      },
      {
        title: "Will this work for my niche?",
        content: "Yes. We work with influencers, brands, musicians, fitness creators, real estate agents, podcasts, local businesses, and more.",
      },
      {
        title: "Is this a subscription?",
        content: "The package itself is a single payment. It also includes our auto-refill service, which keeps your followers topped up as accounts drop off — that renews at $19.99, first billed 7 days after your purchase and every 28 days after that. You can cancel it anytime from your dashboard and keep the followers you have.",
      },
      {
        title: "Do the followers stay?",
        content: "Any followers delivered are yours to keep. Some accounts do unfollow over time, which is normal on Instagram — that is what the auto-refill service is for. If you cancel it, the followers you already have stay on your account.",
      },
    ],
    publicOnlyItems: [
      {
        title: "Will I be charged again?",
        content: "The package is a one-time payment, and it is not charged again. Your purchase includes the auto-refill service at $19.99, billed 7 days after you buy and every 28 days from then, which you can cancel anytime from your dashboard.",
      },
      {
        title: "How can I get a refund?",
        content: (
          <>
            You can request a refund anytime through our online refund portal at{' '}
            <a href="https://viralgpt.com/refund" className="text-instagram" style={{ textDecoration: 'underline' }}>
              https://viralgpt.com/refund
            </a>{' '}
            or by contacting{' '}
            <a href="mailto:support@viralgpt.com" className="text-instagram" style={{ textDecoration: 'underline' }}>
              support@viralgpt.com
            </a>.
          </>
        ),
      },
    ],
  },
  testimonials: {
    verifiedResults: "VERIFIED RESULTS",
    heading: "Real Customer Results",
    sub: "Real creators. Real accounts. Real, measurable growth.",
    fromReviews: "from 2,400+ reviews",
    reviews: {
      daniel_slovak: "ViralGPT's Instagram targeting is incredible. I grew from 10k to 40k real followers by reaching people who love fitness content like mine.",
      smoke: "ViralGPT is the growth tool every serious creator needs. My engagement skyrocketed and the followers are genuinely interested in my content.",
      vikky: "I was struggling to grow my following. ViralGPT helped me reach the right audience and gain real, engaged followers.",
    },
    growthLabel: "Growth",
    verifiedAlt: "Verified",
    smokeTitle: "TopTree CEO · 359k followers",
    followersCount: (n) => `${n} followers`,
    asFeaturedOn: "As Featured On",
    ctaButton: "Start Growing Today",
    ctaSub: "No password required · One-time purchase · Delivery starts right away",
    whyLabel: "Why It Matters",
    whyHeading: "Why more followers matter",
    whySub: "Turn followers into real business results.",
    whyCards: [
      { title: "Attract Brand Collaborations", desc: <>Unlock more <strong>brand deals</strong> and <strong>sponsorship</strong> opportunities.</> },
      { title: "Drive Link in Bio Clicks", desc: <>Turn followers into <strong>profile visits</strong> and <strong>link clicks</strong>.</> },
      { title: "Convert More Customers", desc: <>Increase <strong>trust</strong> and convert more <strong>visitors</strong> into <strong>customers</strong>.</> },
    ],
  },
  footer: {
    platformHeading: "Grow Your Audience on Any Platform",
    platforms: [
      { title: "Dominate Instagram", sub: "More Followers" },
      { title: "Go Viral on TikTok", sub: "Explosive Growth" },
      { title: "Master YouTube", sub: "More Subscribers" },
      { title: "Build X Empire", sub: "More Followers" },
    ],
    tryHeading: <>Try <span className="text-instagram">ViralGPT</span> today</>,
    riskFree: "Start risk-free with our 7-day money-back guarantee.",
    getStarted: "Get Started",
    description: "The #1 AI-powered platform to grow your social media following organically. Get real followers, engagement, and results with our advanced targeting and automation tools.",
    terms: "Terms of Service",
    privacy: "Privacy Policy",
    refundPolicy: "Refund Policy",
    allRightsReserved: "All rights reserved.",
  },

  // Public one-time follower packages. Claims stay limited to what is
  // delivered: a follower count, a one-time price for it, and the auto-refill
  // service it comes with — which renews, and must always be said so.
  packages: {
    heading: "Pick your package",
    subheading: "One payment for your followers, plus auto-refill to keep them.",
    valueProp: "Real followers · Instant delivery · No password",
    followers: "followers",
    oneTime: "one-time",
    mostPopular: "Most Popular",
    bestValue: "Best Value",
    cta: (followers, price) => `Get ${followers} Followers — $${price}`,
    noSubscription: "One-time purchase. Includes auto-refill at $19.99 after 7 days, cancel anytime.",
  },
};

export default lang;
